// src/components/InsuranceDossierButton.jsx
// Bouton « Dossier d'assurance » (§5/§17) au niveau collection.
// Charge œuvres (artworks_secure, RLS) + polices existantes (table insurance) puis génère
// la cédule PDF. Libellés FR/EN/DE en ligne.
import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { generateInsuranceDossier } from "../utils/insuranceDossierPdf";
import { useTranslation } from "react-i18next";

const COPY = {
  fr: { busy: "Préparation…", label: "Dossier d'assurance (PDF)", title: "Générer une cédule d'assurance (inventaire, valeur à assurer, état, polices)", fail: "Impossible de générer le dossier d'assurance." },
  en: { busy: "Preparing…", label: "Insurance file (PDF)", title: "Generate an insurance schedule (inventory, insured value, condition, policies)", fail: "Unable to generate the insurance file." },
  de: { busy: "Wird vorbereitet…", label: "Versicherungsakte (PDF)", title: "Versicherungsaufstellung erstellen (Inventar, Versicherungswert, Zustand, Policen)", fail: "Die Versicherungsakte konnte nicht erstellt werden." },
};

export default function InsuranceDossierButton({ collectionId, collectionName = "", ownerName = "", className = "" }) {
  const [busy, setBusy] = useState(false);
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const c = COPY[lang] || COPY.fr;

  const run = async () => {
    if (!collectionId || busy) return;
    setBusy(true);
    try {
      const { data: artworks, error: aErr } = await supabase
        .from("artworks_secure")
        .select("id,title,artist,current_estimated_value,valuation_estimated_value,estimated_value,value,condition,storage_location_detail,insurance_status")
        .eq("collection_id", collectionId);
      if (aErr) throw aErr;

      const ids = (artworks || []).map((a) => a.id);
      const insByArtwork = {};
      if (ids.length) {
        const { data: rows } = await supabase
          .from("insurance")
          .select("artwork_id, insurer, insurance_type, value, expiry_date, idnumber_insurance")
          .in("artwork_id", ids);
        for (const r of rows || []) {
          // garde la police la plus récente / valide par œuvre
          if (!insByArtwork[r.artwork_id]) insByArtwork[r.artwork_id] = r;
        }
      }

      generateInsuranceDossier({ collectionName, ownerName, artworks: artworks || [], insByArtwork, lang });
    } catch (e) {
      console.error("[InsuranceDossierButton]", e);
      window.alert(c.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !collectionId}
      title={c.title}
      className={className || "inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"}
    >
      {busy ? c.busy : c.label}
    </button>
  );
}
